import React, { useState } from 'react';
import { Plus, X, Loader2, Globe, Mic, BookOpen, CheckCircle, Info } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SOURCE_TYPES = [
  { value: 'substack', label: 'Substack', icon: BookOpen },
  { value: 'twitter', label: 'Twitter/X', icon: Globe },
  { value: 'blog', label: 'Blog', icon: Globe },
  { value: 'podcast', label: 'Podcast', icon: Mic },
];

const URL_HINTS = {
  substack: 'e.g. https://platformer.news or newsletter.substack.com',
  twitter: 'e.g. https://nitter.net/username/rss',
  blog: 'e.g. https://techcrunch.com/feed/ or any blog URL',
  podcast: 'e.g. RSS feed URL from Apple Podcasts / Spotify',
};

export function AddSourceForm({ onAddSource, onCancel }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [sourceType, setSourceType] = useState('blog');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!url.trim() || !title.trim()) {
      setError('URL and title are required');
      return;
    }
    setAdding(true);
    try {
      const result = await onAddSource(url, title, sourceType);
      const count = result?.entry_count || 0;
      setSuccess(`Added! ${count} entries found.`);
      setUrl('');
      setTitle('');
      setSourceType('blog');
      setTimeout(() => onCancel(), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = (typeof detail === 'string') ? detail
        : Array.isArray(detail) ? detail.map(e => e?.msg || JSON.stringify(e)).join(' ')
        : 'Failed to add source. Try pasting a direct RSS feed URL.';
      setError(message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <form onSubmit={handleAdd} className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-[0.2em]">New Source</span>
        <button
          data-testid="cancel-add-source-button"
          type="button"
          onClick={onCancel}
          className="text-zinc-500 hover:text-black"
        >
          <X strokeWidth={2.5} size={16} />
        </button>
      </div>

      {error && (
        <div data-testid="add-source-error" className="text-xs font-medium text-red-600 bg-red-50 border-2 border-black p-2">
          {error}
        </div>
      )}
      {success && (
        <div data-testid="add-source-success" className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border-2 border-black p-2">
          <CheckCircle strokeWidth={2.5} size={14} />
          {success}
        </div>
      )}

      <input
        data-testid="source-title-input"
        type="text"
        placeholder="Source name"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="brutalist-input text-sm"
      />
      <div>
        <input
          data-testid="source-url-input"
          type="url"
          placeholder="Website or RSS feed URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="brutalist-input text-sm"
        />
        <div className="flex items-start gap-1 mt-1">
          <Info strokeWidth={2.5} size={11} className="text-zinc-400 mt-0.5 flex-shrink-0" />
          <p data-testid="url-hint" className="text-[10px] text-zinc-400 font-medium leading-tight">
            {URL_HINTS[sourceType]} — paste a website URL or direct RSS feed link
          </p>
        </div>
      </div>

      <Select value={sourceType} onValueChange={setSourceType}>
        <SelectTrigger data-testid="source-type-select" className="border-2 border-black rounded-none h-11 font-medium">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="border-2 border-black rounded-none">
          {SOURCE_TYPES.map((t) => (
            <SelectItem key={t.value} value={t.value} data-testid={`source-type-${t.value}`}>
              <span className="flex items-center gap-2">
                <t.icon strokeWidth={2.5} size={14} />
                {t.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <button
        data-testid="add-source-submit-button"
        type="submit"
        disabled={adding}
        className="brutalist-btn-secondary w-full text-sm flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {adding ? <Loader2 strokeWidth={2.5} size={14} className="animate-spin" /> : <Plus strokeWidth={2.5} size={14} />}
        {adding ? 'Adding...' : 'Add Feed'}
      </button>
    </form>
  );
}
